import { Button, Card, Flex, Typography, message } from "antd"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import React, { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { dateTimeFormat } from "../../../utils/utils"
import { RoutesPaths } from "../../../constants/routes"
import { brand, chipVariants, iconTileTints, neutral, shadows, tealGhost } from "../tokens"
import { WidgetFrame } from "../WidgetFrame"
import type {
  ActivityFeedItem,
  FeedItem,
  WidgetProps,
  WidgetState,
} from "../types"
import { isActivityFeedItem } from "../types"

dayjs.extend(relativeTime)

const { Text } = Typography

const SEVERITY_ORDER: Record<FeedItem["severity"], number> = {
  critical: 0,
  warn: 1,
  info: 2,
}

const SEVERITY_TINTS: Record<FeedItem["severity"], { bg: string; fg: string }> = {
  critical: iconTileTints.error,
  warn: iconTileTints.warning,
  info: iconTileTints.teal,
}

const WarningIcon = () => (
  <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
)

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
)

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8h.01M12 11v5" />
  </svg>
)

const SeverityIcon = ({ severity }: { severity: FeedItem["severity"] }) => {
  if (severity === "critical") return <WarningIcon />
  if (severity === "warn") return <ClockIcon />
  return <InfoIcon />
}

const VISIBLE_LIMIT = 5
const SHOW_MORE_STEP = 25

const stringParam = (params: FeedItem["action"]["params"], key: string) => {
  const value = params[key]
  return typeof value === "string" && value ? value : undefined
}

const resolveActionRoute = (item: FeedItem): string | undefined => {
  const { kind, params } = item.action
  const url = stringParam(params, "url")
  if (url) return url

  const leadId = stringParam(params, "lead_id") ?? item.lead_id ?? undefined
  const projectId = stringParam(params, "project_id") ?? item.project_id ?? undefined

  if (kind === "send_reminder" && projectId) {
    return RoutesPaths.project.view(projectId).overview
  }
  if (leadId) return RoutesPaths.lead.view(leadId)
  if (projectId) return RoutesPaths.project.view(projectId).overview
  return undefined
}

interface NBASlotProps {
  items: FeedItem[]
}

const NBASlotImpl = ({ items }: NBASlotProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [visibleCount, setVisibleCount] = useState(VISIBLE_LIMIT)

  const sortedItems = useMemo(
    () =>
      [...items].sort(
        (a, b) =>
          (SEVERITY_ORDER[a.severity] ?? SEVERITY_ORDER.info) -
          (SEVERITY_ORDER[b.severity] ?? SEVERITY_ORDER.info)
      ),
    [items]
  )

  const handleAction = (item: FeedItem) => {
    const target = resolveActionRoute(item)
    if (target) {
      navigate(target)
      return
    }
    message.info(t("portfolio.nba.actionNotAvailable", "Action not available yet"))
  }

  if (!sortedItems.length) return null

  const visibleItems = sortedItems.slice(0, visibleCount)
  const hiddenCount = sortedItems.length - visibleItems.length
  const canCollapse = hiddenCount === 0 && sortedItems.length > VISIBLE_LIMIT

  return (
    <Card
      size="small"
      title={t("portfolio.nba.title", "Next Best Actions")}
      style={{
        border: `1px solid ${neutral.border}`,
        borderRadius: 12,
        boxShadow: shadows.card,
      }}
      styles={{
        header: { minHeight: 32, padding: "4px 14px" },
        body: { padding: "6px 12px" },
      }}
    >
      {/* A compact actionable notification, not a dashboard panel — each row
          targets ~44-52px total ~80-100px (2026-09-02), so "one thing to act
          on" doesn't read as "another large section". */}
      <Flex vertical gap={4}>
        {visibleItems.map((item) => {
          const tint = SEVERITY_TINTS[item.severity] ?? SEVERITY_TINTS.info
          return (
            <Flex
              key={item.id}
              data-testid={`nba-item-${item.id}`}
              align="center"
              gap={10}
              style={{
                padding: "6px 10px",
                border: `1px solid ${neutral.border}`,
                borderRadius: 10,
                background: neutral.surface,
              }}
            >
              <div
                role="img"
                aria-label={t(`portfolio.nba.severity.${item.severity}`, item.severity)}
                data-testid="severity-icon"
                data-severity={item.severity}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  flex: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: tint.bg,
                  color: tint.fg,
                }}
              >
                <SeverityIcon severity={item.severity} />
              </div>
              {/* No `flex: 1` here on purpose — that stretches this block to
                  consume the entire row, which is what shoves the button to
                  the card's far edge on a full-width card. The content stays
                  grouped at its natural width and the button follows right
                  after it with the row's own `gap`; a bounded `maxWidth` is
                  what keeps `ellipsis` meaningful without the flex-grow. */}
              <Flex vertical style={{ minWidth: 0, maxWidth: 420 }}>
                <Flex align="center" gap={6} style={{ minWidth: 0 }}>
                  <Text strong ellipsis={{ tooltip: item.title }} style={{ fontSize: 13 }}>
                    {item.title}
                  </Text>
                  {item.action.category && (
                    <span
                      data-testid="nba-category"
                      data-category={item.action.category}
                      style={{
                        flex: "none",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        padding: "1px 7px",
                        borderRadius: 999,
                        background: chipVariants.muted.bg,
                        color: chipVariants.muted.fg,
                      }}
                    >
                      {t(`portfolio.nba.category.${item.action.category}`, item.action.category)}
                    </span>
                  )}
                </Flex>
                <Text
                  type="secondary"
                  ellipsis={{ tooltip: item.rationale }}
                  style={{ fontSize: 12 }}
                >
                  {item.rationale}
                </Text>
              </Flex>
              <button
                type="button"
                onClick={() => handleAction(item)}
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: brand.teal,
                  border: `1px solid ${tealGhost.border}`,
                  background: tealGhost.bg,
                  padding: "4px 12px",
                  borderRadius: 8,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {t("portfolio.nba.action", "Take Action")}
              </button>
            </Flex>
          )
        })}
      </Flex>
      {hiddenCount > 0 && (
        <Button
          type="link"
          size="small"
          aria-expanded={false}
          style={{ paddingInline: 0, marginTop: 4 }}
          onClick={() =>
            setVisibleCount((prev) => Math.min(prev + SHOW_MORE_STEP, sortedItems.length))
          }
        >
          {t("portfolio.nba.showMore", "Show more ({{count}})", { count: hiddenCount })}
        </Button>
      )}
      {canCollapse && (
        <Button
          type="link"
          size="small"
          aria-expanded={true}
          style={{ paddingInline: 0, marginTop: 4 }}
          onClick={() => setVisibleCount(VISIBLE_LIMIT)}
        >
          {t("portfolio.nba.showLess", "Show less")}
        </Button>
      )}
    </Card>
  )
}

export const NBASlot = React.memo(NBASlotImpl)

const ActivityRow = ({ item }: { item: ActivityFeedItem }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const target = item.drill_to
  const absolute = dayjs(item.timestamp)
  const relative = absolute.isValid() ? absolute.fromNow() : item.timestamp
  const tooltip = absolute.isValid() ? absolute.format(dateTimeFormat) : undefined

  const handleOpen = () => {
    if (target) navigate(target)
  }

  return (
    <Flex
      data-testid={`feed-item-${item.id}`}
      role={target ? "button" : undefined}
      tabIndex={target ? 0 : undefined}
      onClick={target ? handleOpen : undefined}
      onKeyDown={
        target
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                handleOpen()
              }
            }
          : undefined
      }
      vertical
      gap={2}
      style={{
        padding: "10px 12px",
        border: `1px solid ${neutral.border}`,
        borderRadius: 10,
        background: neutral.surface,
        cursor: target ? "pointer" : "default",
      }}
    >
      <Text strong ellipsis={{ tooltip: item.title }}>
        {item.title}
      </Text>
      <Text
        type="secondary"
        data-testid="feed-timestamp"
        title={tooltip}
        aria-label={t("portfolio.feed.occurredAt", "Occurred at {{time}}", {
          time: tooltip ?? relative,
        })}
        style={{ fontSize: 12 }}
      >
        {relative}
      </Text>
    </Flex>
  )
}

const ActivityFeedImpl = ({
  widget,
  payload,
  state,
  onRefresh,
  onExpand
}: WidgetProps) => {
  const { t } = useTranslation()
  const items = (payload?.feed?.items ?? []).filter(isActivityFeedItem)
  const title = widget.config?.title ?? widget.title

  let effectiveState: WidgetState
  if (state === "loading" || state === "error") {
    effectiveState = state
  } else if (!payload?.feed) {
    effectiveState = "loading"
  } else if (items.length === 0) {
    effectiveState = "empty"
  } else {
    effectiveState = state ?? "ready"
  }

  return (
    <WidgetFrame
      title={title}
      state={effectiveState}
      capabilities={widget.capabilities}
      widgetId={widget.id}
      payload={payload}
      emptyText={t("portfolio.feed.empty", "No recent activity")}
      onRefresh={onRefresh}
      onExpand={onExpand}
    >
      <Flex vertical gap={8}>
        {items.map((item) => (
          <ActivityRow key={item.id} item={item} />
        ))}
      </Flex>
    </WidgetFrame>
  )
}

export const ActivityFeed = React.memo(ActivityFeedImpl)

const EMPTY_ITEMS: FeedItem[] = []

export const FeedWidget = (props: WidgetProps) => {
  const items = props.payload?.feed?.items ?? EMPTY_ITEMS
  const { dataSource } = props.widget
  const isNbaFeed =
    dataSource === "nba" ||
    (dataSource == null && items.some((item) => !isActivityFeedItem(item)))
  if (isNbaFeed) {
    return <NBASlot items={items as FeedItem[]} />
  }
  return <ActivityFeed {...props} />
}
